import { Box, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { ComponentType } from 'react';

export type ApplicationStatusCardKey = 'draft' | 'pending' | 'completed' | 'expired';

export type ApplicationStatusCounts = Record<ApplicationStatusCardKey, number>;

export type StatusCardConfig = {
  key: ApplicationStatusCardKey;
  label: string;
  iconSrc?: string;
  Icon?: ComponentType<{ sx?: Record<string, unknown>; 'aria-hidden'?: boolean }>;
  iconBg: string;
  iconColor?: string;
  filterStatuses: string[];
};

export const APPLICATION_STATUS_CARDS: StatusCardConfig[] = [
  {
    key: 'draft',
    label: 'Draft',
    iconSrc: '/images/draft.svg',
    iconBg: '#F4EAFF',
    filterStatuses: ['draft'],
  },
  {
    key: 'pending',
    label: 'Pending',
    iconSrc: '/images/pending.svg',
    iconBg: '#FFA0001A',
    filterStatuses: [
      'sent_to_applicant1',
      'approved_by_applicant1',
      'approved_by_applicant2',
      'approved_by_applicant3',
      'approved_by_fundmanager',
      'sent_to_poa_approver',
      'sent_to_amc_approver',
      'sent_to_authorised_signatories',
      'approved_by_authorised_signatories',
    ],
  },
  {
    key: 'completed',
    label: 'Completed',
    iconSrc: '/images/completedapp.svg',
    iconBg: '#ECFDF5',
    filterStatuses: ['completed,signed'],
  },
  {
    key: 'expired',
    label: 'Failed',
    Icon: CloseIcon,
    iconBg: '#FFEBEE',
    iconColor: '#C62828',
    filterStatuses: ['invitationexpired', 'rejected'],
  },
];

type ApplicationStatusCardsProps = {
  counts: ApplicationStatusCounts;
  activeKey?: ApplicationStatusCardKey | null;
  onCardClick?: (card: StatusCardConfig) => void;
  title?: string;
};

export default function ApplicationStatusCards({
  counts,
  activeKey = null,
  onCardClick,
  title = '',
}: ApplicationStatusCardsProps): JSX.Element {
  return (
    <Box sx={{ mb: { xs: 2, md: 3 } }}>
      <Typography
        component="h2"
        sx={{
          fontFamily: 'Roboto, sans-serif',
          fontSize: { xs: 18, sm: 20 },
          fontWeight: 700,
          color: '#111927',
          mb: 2,
        }}>
        {title}
      </Typography>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            md: 'repeat(4, 1fr)',
          },
          gap: { xs: 1.5, sm: 2 },
          width: '100%',
        }}>
        {APPLICATION_STATUS_CARDS.map((card) => {
          const isActive = activeKey === card.key;
          const clickable = Boolean(onCardClick);

          return (
            <Box
              key={card.key}
              component={clickable ? 'button' : 'div'}
              type={clickable ? 'button' : undefined}
              onClick={clickable ? () => onCardClick?.(card) : undefined}
              aria-pressed={clickable ? isActive : undefined}
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
                minWidth: 0,
                height: 'auto',
                boxSizing: 'border-box',
                p: '24px',
                borderRadius: '16px',
                background: '#FFFFFF',
                border: isActive ? '1px solid #2057A6' : '1px solid transparent',
                boxShadow: '0px 4px 20px rgba(15, 23, 42, 0.06)',
                cursor: clickable ? 'pointer' : 'default',
                textAlign: 'left',
                font: 'inherit',
                transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
                '&:hover': clickable
                  ? {
                      boxShadow: '0px 6px 24px rgba(15, 23, 42, 0.1)',
                    }
                  : undefined,
                '&:focus-visible': clickable
                  ? {
                      outline: '3px solid #2057A6',
                      outlineOffset: '2px',
                    }
                  : undefined,
              }}>
              <Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Typography
                  sx={{
                    fontFamily: 'Inter, sans-serif',
                    fontSize: 14,
                    fontWeight: 500,
                    color: '#6B7280',
                    lineHeight: '20px',
                  }}>
                  {card.label}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: 'Inter, sans-serif',
                    fontSize: 32,
                    fontWeight: 700,
                    color: '#092056',
                    lineHeight: '40px',
                  }}>
                  {counts[card.key] ?? 0}
                </Typography>
              </Box>

              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: '24px',
                  background: card.iconBg,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}>
                {card.iconSrc ? (
                  <Box
                    component="img"
                    src={card.iconSrc}
                    alt=""
                    aria-hidden
                    sx={{ width: 24, height: 24, objectFit: 'contain' }}
                  />
                ) : card.Icon ? (
                  <card.Icon sx={{ fontSize: 24, color: card.iconColor }} aria-hidden />
                ) : null}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
